import React, { useRef, useEffect, useState } from "react";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  updateProfile,
  getAdditionalUserInfo,
  deleteUser,
  signOut,
} from "firebase/auth";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useRecaptchaVerifier } from "../../hooks/reCaptchahook";
import { socialSignInEnabled } from "../../utils/features";

// Extend the window type to include recaptchaVerifier and confirmationResult
declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
    confirmationResult?: ConfirmationResult;
  }
}

/**
 * Whether the public may register.
 *
 * Mirrors PUBLIC_ACCESS_ENABLED on the server, which is the thing that actually
 * enforces it — this only decides what the form offers, so a stale value here
 * can mislead but cannot let anyone in.
 */
const PUBLIC_ACCESS_ENABLED = process.env.REACT_APP_PUBLIC_ACCESS_ENABLED === "true";
const STAFF_EMAIL_DOMAIN = (process.env.REACT_APP_STAFF_EMAIL_DOMAIN || "buildmytravel.ae").toLowerCase();

const isCompanyEmail = (value: string) =>
  value.trim().toLowerCase().endsWith(`@${STAFF_EMAIL_DOMAIN}`);

const Signup = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [showPhoneSignup, setShowPhoneSignup] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading, ensureSession, requestLoginOtp } = useAuth();

  /**
   * Redirect only somebody who was already signed in when this screen opened.
   *
   * Firebase sets `user` the instant the account is created, so redirecting on
   * `user` alone tore this screen down halfway through its own signup — before
   * the profile was posted and before "check your email" could be shown. Same
   * race as the login screen, where it cost the sign-in code its input box.
   *
   * The handlers below navigate explicitly when they are finished.
   */
  const arrivedSignedIn = useRef<boolean | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (arrivedSignedIn.current === null) {
      arrivedSignedIn.current = !!user;
    }
    if (arrivedSignedIn.current && user) navigate("/");
  }, [authLoading, user, navigate]);

  useRecaptchaVerifier("recaptcha-container");

  // Email/Password Signup
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Checked before creating the Firebase user, not after. Creating the
    // account first would leave an orphaned Firebase login with no profile
    // behind it, and the same address could then never be registered properly.
    if (!PUBLIC_ACCESS_ENABLED && !isCompanyEmail(email)) {
      setError(`Only @${STAFF_EMAIL_DOMAIN} email addresses can register at the moment.`);
      return;
    }

    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      if (fullName) {
        await updateProfile(result.user, { displayName: fullName });
      }

      // Use AuthContext as the single session creator (handles retries, coalescing)
      const ok = await ensureSession(result.user);
      if (!ok) {
        setError("Failed to create session. Please try again.");
        setLoading(false);
        return;
      }

      // Session is ready — create the backend profile
      try {
        await axios.post(
          "/create-profile",
          {
            uid: result.user.uid,
            email: result.user.email,
            // Typed on the form. The OTP is sent here, so it is mandatory —
            // an account without one cannot sign in once OTP is switched on.
            phoneNumber: mobile || result.user.phoneNumber,
            displayName: fullName,
          },
          { withCredentials: true },
        );
      } catch (profileErr: any) {
        // Profile creation failed, but session + Firebase user are fine.
        // The profile will be auto-created by getProfileUsecase on first fetch.
        console.warn(
          "[Signup] Profile creation failed (will be auto-created later):",
          profileErr.message,
        );
      }

      // Send verification email (fire-and-forget — don't block navigation)
      try {
        await axios.post(
          "/api/send-verification-email",
          { email: result.user.email },
          { withCredentials: true },
        );
        setVerificationSent(true);
      } catch (verifyErr: any) {
        console.warn(
          "[Signup] Verification email failed to send:",
          verifyErr.message,
        );
        // Still navigate even if verification email fails
        navigate("/profile");
      }
    } catch (err: any) {
      setError(err.message);
      // Only sign out if the Firebase account itself is problematic
      if (err.code === "auth/email-already-in-use") {
        // Don't sign out — user might want to log in instead
      }
    }
    setLoading(false);
  };

  // Google Signup
  const handleGoogleSignup = async () => {
    setError("");
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // The domain can only be checked *after* the popup, because until then we
      // do not know which Google account was chosen. Firebase has already
      // created the user by that point, so a refusal has to clean up after
      // itself — an orphaned Firebase login with no profile behind it means
      // that address can never be registered properly afterwards, which is the
      // same trap the email path avoids by checking first.
      //
      // Only a brand-new account is deleted. An existing one arriving here is
      // somebody signing in, and deleting their login over a domain rule would
      // destroy an account rather than decline a registration.
      if (!PUBLIC_ACCESS_ENABLED && !isCompanyEmail(result.user.email ?? "")) {
        const isNew = getAdditionalUserInfo(result)?.isNewUser === true;
        try {
          if (isNew) await deleteUser(result.user);
          else await signOut(auth);
        } catch {
          // Deletion needs a recent sign-in, which we have just had; if it
          // fails anyway, signing out still leaves the browser in a truthful
          // state and a super user can remove the account.
          try { await signOut(auth); } catch { /* already gone */ }
        }
        setError(`Only @${STAFF_EMAIL_DOMAIN} email addresses can register at the moment.`);
        setLoading(false);
        return;
      }

      // Signing in with Google or a phone number can land on an account that
      // already exists — and if it is staff, the server wants a one-time code
      // before it will issue a session. The signup screen has no place to
      // collect one, and without this it reports "Failed to create session",
      // which is both wrong and unactionable. Send them to sign in instead.
      const challenge = await requestLoginOtp(result.user);
      if (challenge.otpRequired) {
        setLoading(false);
        navigate("/login", { state: { notice: "This account needs a sign-in code. Please sign in." } });
        return;
      }

      const ok = await ensureSession(result.user);
      if (!ok) {
        setError("Failed to create session. Please try again.");
        setLoading(false);
        return;
      }

      // Create backend profile (non-critical — will be auto-created if this fails)
      try {
        await axios.post(
          "/create-profile",
          {
            uid: result.user.uid,
            email: result.user.email,
            phoneNumber: result.user.phoneNumber,
            displayName: result.user.displayName,
          },
          { withCredentials: true },
        );
      } catch (profileErr: any) {
        console.warn(
          "[Google Signup] Profile creation failed (will be auto-created later):",
          profileErr.message,
        );
      }

      navigate("/profile");
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  // OTP Signup (send OTP)
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!phone.trim()) {
      setError("Please enter your mobile phone number.");
      setLoading(false);
      return;
    }

    const appVerifier = window.recaptchaVerifier!;
    try {
      await signInWithPhoneNumber(auth, phone, appVerifier)
        .then((confirmationResult) => {
          window.confirmationResult = confirmationResult;
          setOtpSent(true);
        })
        .catch((err) => {
          setError(err.message);
        });
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  // OTP Signup (verify OTP)
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const userCredential = await window.confirmationResult!.confirm(otp);
      if (userCredential.user && fullName) {
        await updateProfile(userCredential.user, { displayName: fullName });
      }

      // Signing in with Google or a phone number can land on an account that
      // already exists — and if it is staff, the server wants a one-time code
      // before it will issue a session. The signup screen has no place to
      // collect one, and without this it reports "Failed to create session",
      // which is both wrong and unactionable. Send them to sign in instead.
      const challenge = await requestLoginOtp(userCredential.user);
      if (challenge.otpRequired) {
        setLoading(false);
        navigate("/login", { state: { notice: "This account needs a sign-in code. Please sign in." } });
        return;
      }

      const ok = await ensureSession(userCredential.user);
      if (!ok) {
        setError("Failed to create session. Please try again.");
        setLoading(false);
        return;
      }

      try {
        await axios.post(
          "/create-profile",
          {
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            phoneNumber: userCredential.user.phoneNumber,
            displayName: fullName,
          },
          { withCredentials: true },
        );
      } catch (profileErr: any) {
        console.warn(
          "[OTP Signup] Profile creation failed (will be auto-created later):",
          profileErr.message,
        );
      }

      navigate("/profile");
    } catch (err: any) {
      setError("Invalid OTP. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-card">
      {verificationSent ? (
        <div className="bg-white p-8 rounded-xl shadow-md w-full max-w-md text-center">
          <div className="text-green-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-4 text-primary">Verify Your Email</h2>
          <p className="text-gray-600 mb-2">
            A verification link has been sent to <strong>{email}</strong>.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            Please check your inbox and click the link to verify your email address.
            If you don't see it, check your spam folder.
          </p>
          <button
            type="button"
            className="w-full bg-primary text-white py-2 rounded font-bold hover:bg-blue-800 transition"
            onClick={() => navigate("/profile")}
          >
            Continue to Profile
          </button>
        </div>
      ) : !showPhoneSignup ? (
        <form
          onSubmit={handleSignup}
          className="bg-white p-8 rounded-xl shadow-md w-full max-w-md"
        >
          <h2 className="text-2xl font-bold mb-2 text-primary">Sign Up</h2>

          {!PUBLIC_ACCESS_ENABLED && (
            <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              Staff registration only. Use your <strong>@{STAFF_EMAIL_DOMAIN}</strong> address.
              Head office will need to approve your account before you can start booking.
            </div>
          )}

          {error && <div className="text-red-500 mb-4">{error}</div>}

          {/* Email/Password Signup */}
          <div className="mb-4">
            <label className="block mb-1 font-semibold">Email</label>
            <input
              type="email"
              className="w-full border rounded px-3 py-2"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-1 font-semibold">Full Name</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-1 font-semibold">Mobile Number</label>
            <input
              type="tel"
              className="w-full border rounded px-3 py-2"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="05X XXX XXXX"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Your security code is sent here, so it must be a number you can reach.
            </p>
          </div>
          <div className="mb-6">
            <label className="block mb-1 font-semibold">Password</label>
            <input
              type="password"
              className="w-full border rounded px-3 py-2"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-primary text-white py-2 rounded font-bold hover:bg-blue-800 transition"
            disabled={loading}
          >
            {loading ? "Signing Up..." : "Sign Up"}
          </button>

          {/* Auth Icons — hidden while registration is staff-only: Google
              never collects a phone number, and phone signup produces an
              account with no email address, so neither can become staff.
              socialSignInEnabled is the dedicated switch and is off by
              default, so these stay hidden even if public access is reopened
              before Google and phone accounts can satisfy the rules above. */}
          {PUBLIC_ACCESS_ENABLED && socialSignInEnabled && (
          <div className="flex justify-center gap-8 mt-8">
            {/* Google Icon */}
            <button
              type="button"
              className="flex flex-col items-center focus:outline-none"
              onClick={handleGoogleSignup}
              disabled={loading}
              aria-label="Sign up with Google"
            >
              <svg width="40" height="40" viewBox="0 0 48 48">
                <g>
                  <circle cx="24" cy="24" r="24" fill="#fff" />
                  <path
                    fill="#4285F4"
                    d="M34.6 24.2c0-.7-.1-1.4-.2-2H24v4.1h6c-.3 1.5-1.3 2.7-2.7 3.5v2.9h4.4c2.6-2.4 4.1-5.9 4.1-10.5z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 36c3.6 0 6.6-1.2 8.8-3.2l-4.4-2.9c-1.2.8-2.7 1.3-4.4 1.3-3.4 0-6.2-2.3-7.2-5.4h-4.5v3.1C15.3 33.7 19.3 36 24 36z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M16.8 25.8c-.3-.8-.5-1.7-.5-2.8s.2-2 .5-2.8v-3.1h-4.5C11.3 19.3 10 21.5 10 24s1.3 4.7 3.3 6.1l4.5-3.1z"
                  />
                  <path
                    fill="#EA4335"
                    d="M24 17.9c1.9 0 3.6.6 4.9 1.7l3.7-3.7C30.6 13.8 27.6 12.5 24 12.5c-4.7 0-8.7 2.3-11.2 5.7l4.5 3.1c1-3.1 3.8-5.4 7.2-5.4z"
                  />
                  <path fill="none" d="M10 10h28v28H10z" />
                </g>
              </svg>
              <span className="text-xs mt-1">Google</span>
            </button>
            {/* Phone Icon.
                Hidden while registration is restricted to the company domain:
                a phone-only account carries no email address at all, so it can
                never satisfy that rule. Offering it would send somebody through
                reCAPTCHA and a real SMS to be refused at the end. */}
            {PUBLIC_ACCESS_ENABLED && (
            <button
              type="button"
              className="flex flex-col items-center focus:outline-none"
              onClick={() => setShowPhoneSignup(true)}
              aria-label="Sign up with Phone"
            >
              <svg width="40" height="40" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="24" fill="#fff" />
                <path
                  d="M34 32.5v2.1c0 .8-.7 1.4-1.5 1.4-7.2 0-13-5.8-13-13 0-.8.6-1.5 1.4-1.5h2.1c.7 0 1.3.5 1.4 1.2.1.7.3 1.4.6 2 .2.5.1 1.1-.3 1.5l-1 1c1.2 2.3 3.1 4.2 5.4 5.4l1-1c.4-.4 1-.5 1.5-.3.6.2 1.3.5 2 .6.7.1 1.2.7 1.2 1.4z"
                  fill="#4CAF50"
                />
              </svg>
              <span className="text-xs mt-1">Phone</span>
            </button>
            )}
          </div>
          )}

          <div className="mt-4 text-center">
            <span>Already have an account? </span>
            <button
              type="button"
              className="text-primary font-semibold"
              onClick={() => navigate("/login")}
            >
              Login
            </button>
          </div>
        </form>
      ) : !otpSent ? (
        <form
          onSubmit={handleSendOtp}
          className="bg-white p-8 rounded-xl shadow-md w-full max-w-md"
        >
          <h2 className="text-2xl font-bold mb-6 text-primary">
            Sign Up with Phone
          </h2>
          {error && <div className="text-red-500 mb-4">{error}</div>}
          <div className="mb-4">
            <label className="block mb-1 font-semibold">Full Name</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-1 font-semibold">Phone Number</label>
            <input
              type="tel"
              className="w-full border rounded px-3 py-2"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+971xxxxxxxxx"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-green-600 text-white py-2 rounded font-bold hover:bg-green-700 transition"
            disabled={loading}
          >
            {loading ? "Sending OTP..." : "Send OTP"}
          </button>
          <div className="mt-4 text-center">
            <button
              type="button"
              className="text-primary font-semibold"
              onClick={() => {
                setShowPhoneSignup(false);
                setOtpSent(false);
                setPhone("");
                setOtp("");
                setError("");
              }}
            >
              Back to Email Signup
            </button>
          </div>
        </form>
      ) : (
        <form
          onSubmit={handleVerifyOtp}
          className="bg-white p-8 rounded-xl shadow-md w-full max-w-md"
        >
          <h2 className="text-2xl font-bold mb-6 text-primary">Verify OTP</h2>
          {error && <div className="text-red-500 mb-4">{error}</div>}
          <div className="mb-4">
            <label className="block mb-1 font-semibold">Enter OTP</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-green-600 text-white py-2 rounded font-bold hover:bg-green-700 transition"
            disabled={loading}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
          <div className="mt-4 text-center">
            <button
              type="button"
              className="text-primary font-semibold"
              onClick={() => {
                setShowPhoneSignup(false);
                setOtpSent(false);
                setPhone("");
                setOtp("");
                setError("");
              }}
            >
              Back to Email Signup
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Signup;
